import { DownOutlined } from "@ant-design/icons";
import { DiscordCategory } from "@prisma/client";
import {
  Alert,
  Button,
  Dropdown,
  Menu,
  message,
  Popconfirm,
  Table,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import useDotsAdmin from "./useDotsAdmin";

type AdminCategoriesProps = {
  categories?: DiscordCategory[];
  dotsAdminChannelExist: boolean;
};

/**
 * Component for managing dots guilds categories
 */
export default function AdminCategories({
  categories,
  dotsAdminChannelExist,
}: AdminCategoriesProps) {
  const [timePeriod, setTimePeriod] = useState(null);

  const { deleteChannelsMutation } = useDotsAdmin();

  const categoriesDeleteTimeOptions = [
    {
      key: "hour",
      value: "1 Hour",
    },
    { key: "day", value: "24 Hours" },
    { key: "3days", value: "3 Days" },
    { key: "week", value: "1 Week" },
  ];

  const dropDownMenu = (
    <Menu onClick={(e: any) => setTimePeriod(e.key)}>
      {categoriesDeleteTimeOptions.map((option) => (
        <Menu.Item key={option.key}>{option.value}</Menu.Item>
      ))}
    </Menu>
  );

  const deleteChannelsByCategory = (category: DiscordCategory) => {
    const guildId = localStorage.getItem("guild-id") as string;
    let olderThan: number;
    const currentDate = new Date();
    if (timePeriod === "hour") {
      currentDate.setHours(currentDate.getHours() - 1);
      olderThan = currentDate.getTime();
    } else if (timePeriod === "day") {
      currentDate.setHours(currentDate.getHours() - 24);
      olderThan = currentDate.getTime();
    } else if (timePeriod === "3days") {
      currentDate.setHours(currentDate.getHours() - 24 * 3);
      olderThan = currentDate.getTime();
    } else {
      currentDate.setHours(currentDate.getHours() - 24 * 7);
      olderThan = currentDate.getTime();
    }

    const params = {
      guildId,
      categoryId: category.id,
      startTime: olderThan,
    };

    deleteChannelsMutation.mutate(params, {
      onError(e) {
        console.log("Error deleting channels ", e);
        message.error(
          `We couldn't delete the channels in ${category.name}. Try again if the issue persists, contact Dots support.`
        );
      },
      onSuccess() {
        message.success(
          `Got it, we’re working on deleting channels in ${category.name}. This might take a minute, so we’ll ping you in #dots-admins once we’re done!`
        );
      },
    });
  };

  const columns: ColumnsType<DiscordCategory> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      render: (record) => {
        return (
          <Popconfirm
            okButtonProps={{ disabled: !timePeriod || !dotsAdminChannelExist }}
            onVisibleChange={(open: boolean) => {
              if (!open) setTimePeriod(null);
            }}
            onConfirm={() => deleteChannelsByCategory(record)}
            icon={null}
            disabled={record.status !== "active"}
            title={
              <>
                {dotsAdminChannelExist && (
                  <p>
                    Delete channels older than{" "}
                    <Dropdown className="joyride-date" overlay={dropDownMenu}>
                      <Button style={{ width: "10rem" }}>
                        {timePeriod
                          ? categoriesDeleteTimeOptions.find(
                              (t) => t.key === timePeriod
                            )?.value
                          : "Choose a period"}
                        <DownOutlined />
                      </Button>
                    </Dropdown>
                  </p>
                )}

                {!dotsAdminChannelExist && (
                  <Alert
                    message="Dots Admins channel not found"
                    description="You have to create a dots-admin channels to receive the notification when the channels are deleted."
                    type="error"
                  />
                )}
              </>
            }
          >
            <Button
              loading={
                deleteChannelsMutation.variables?.categoryId === record.id &&
                deleteChannelsMutation.isLoading
              }
              disabled={
                record.status !== "active" || deleteChannelsMutation.isLoading
              }
            >
              Delete channels
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  if (!categories || !categories.length) return null;

  return (
    <>
      <div className="ant-card-head-title">Categories</div>
      <Table
        // This is equivalent to ORDER BY status DESC, created_at DESC
        // We want show the active categories first, then the inactive ones
        dataSource={categories.sort((ca, cb) => {
          if (ca.status === "active" && cb.status === "active") {
            return ca.createdAt! > cb.createdAt! ? -1 : 1;
          }
          if (ca.status === "active") return -1;
          return 1;
        })}
        columns={columns}
      />
    </>
  );
}
